/**
 * Base API Service
 *
 * Provides standardized methods for making API requests with authentication,
 * error handling, and consistent response formatting.
 */

import API_BASE_URL from '../config/api';

/**
 * API Error class for handling API-specific errors
 */
export class ApiError extends Error {
  constructor(message, status, data = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

/**
 * Base API request function
 *
 * @param {string} endpoint - API endpoint path
 * @param {Object} options - Request options
 * @param {string} [options.method='GET'] - HTTP method
 * @param {Object} [options.body] - Request body (will be JSON stringified)
 * @param {string} [options.token] - Authorization token
 * @param {Object} [options.headers] - Additional headers
 * @returns {Promise<*>} Response data
 * @throws {ApiError} If request fails
 *
 * @example
 * // GET request
 * const items = await apiRequest('/api/inventory', { token });
 *
 * @example
 * // POST request with body
 * const newItem = await apiRequest('/api/inventory', {
 *   method: 'POST',
 *   body: { item: 'Milk', quantity: 2 },
 *   token,
 * });
 */
export async function apiRequest(endpoint, options = {}) {
  const {
    method = 'GET',
    body,
    token,
    headers = {},
  } = options;

  const requestHeaders = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...headers,
  };

  const config = {
    method,
    headers: requestHeaders,
  };

  if (body && method !== 'GET') {
    config.body = JSON.stringify(body);
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

  // Handle non-JSON responses
  const contentType = response.headers.get('content-type');
  let data;

  if (contentType && contentType.includes('application/json')) {
    data = await response.json();
  } else {
    data = await response.text();
  }

  if (!response.ok) {
    // For JSON error bodies use the server's message; for non-JSON bodies
    // (e.g. an HTML 404 page) use a concise status message rather than
    // dumping the raw HTML into the UI.
    const errorMessage = (typeof data === 'object' && data)
      ? (data.error || data.message)
      : `Request failed (${response.status})`;
    throw new ApiError(errorMessage || 'Request failed', response.status, data);
  }

  return data;
}

/**
 * Create a service with pre-configured token
 *
 * @param {string} token - Authorization token
 * @returns {Object} Service methods with token pre-configured
 *
 * @example
 * const api = createApiService(token);
 * const items = await api.get('/api/inventory');
 * const newItem = await api.post('/api/inventory', { item: 'Milk' });
 */
export function createApiService(token) {
  return {
    /**
     * Make a GET request
     * @param {string} endpoint - API endpoint
     * @param {Object} [options] - Additional options
     * @returns {Promise<*>} Response data
     */
    get: (endpoint, options = {}) => apiRequest(endpoint, { ...options, method: 'GET', token }),

    /**
     * Make a POST request
     * @param {string} endpoint - API endpoint
     * @param {Object} [body] - Request body
     * @param {Object} [options] - Additional options
     * @returns {Promise<*>} Response data
     */
    post: (endpoint, body, options = {}) => apiRequest(endpoint, {
      ...options, method: 'POST', body, token,
    }),

    /**
     * Make a PUT request
     * @param {string} endpoint - API endpoint
     * @param {Object} [body] - Request body
     * @param {Object} [options] - Additional options
     * @returns {Promise<*>} Response data
     */
    put: (endpoint, body, options = {}) => apiRequest(endpoint, {
      ...options, method: 'PUT', body, token,
    }),

    /**
     * Make a PATCH request
     * @param {string} endpoint - API endpoint
     * @param {Object} [body] - Request body
     * @param {Object} [options] - Additional options
     * @returns {Promise<*>} Response data
     */
    patch: (endpoint, body, options = {}) => apiRequest(endpoint, {
      ...options, method: 'PATCH', body, token,
    }),

    /**
     * Make a DELETE request
     * @param {string} endpoint - API endpoint
     * @param {Object} [options] - Additional options
     * @returns {Promise<*>} Response data
     */
    delete: (endpoint, options = {}) => apiRequest(endpoint, { ...options, method: 'DELETE', token }),
  };
}

export default apiRequest;
